import { SearchOutlined } from "@ant-design/icons";
import { Flex, Input, Radio, Spin } from "antd";
import { useMemo, useState } from "react";
import { ColumnFilterWrapper } from "./ColumnFilterWrapper";
import styles from "./searchMultiSelectFilter.module.scss";

export type SearchSingleSelectOption = {
  label: string;
  value: string;
};

type SearchSingleSelectFilterProps = {
  title?: React.ReactNode;
  confirm: () => void;
  close: () => void;
  options: SearchSingleSelectOption[];
  selectedValue?: string;
  onApply: (value?: string) => void;
  loading?: boolean;
  searchPlaceholder?: string;
  before?: React.ReactNode;
  filterCount?: number;
  sortActive?: boolean;
  sortDirection?: "asc" | "desc";
  onSortChange?: (dir?: "asc" | "desc") => void;
  onReset?: () => void;
};

export function SearchSingleSelectFilter({
  title,
  confirm,
  close,
  options,
  selectedValue,
  onApply,
  loading = false,
  searchPlaceholder = "Search",
  before,
  filterCount,
  sortActive,
  sortDirection,
  onSortChange,
  onReset,
}: SearchSingleSelectFilterProps) {
  const [tempSelected, setTempSelected] = useState<string | undefined>(
    selectedValue
  );
  const [searchQuery, setSearchQuery] = useState("");

  const cleanPlaceholder = typeof searchPlaceholder === "string"
    ? searchPlaceholder.replace(/^(Select|Search)\s+/i, "")
    : "";
  const displayTitle =
    title ||
    (cleanPlaceholder && cleanPlaceholder !== "Search"
      ? cleanPlaceholder.charAt(0).toUpperCase() + cleanPlaceholder.slice(1)
      : undefined);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const selectOption = (value: string) => {
    setTempSelected((prev) => (prev === value ? undefined : value));
  };

  const handleClear = () => {
    setTempSelected(undefined);
  };

  const handleApply = () => {
    onApply(tempSelected);
    setSearchQuery("");
    confirm();
  };

  const handleResetInternal = () => {
    setTempSelected(undefined);
    onApply(undefined);
    setSearchQuery("");
    if (onReset) onReset();
    confirm();
  };

  return (
    <ColumnFilterWrapper
      title={displayTitle}
      filterCount={filterCount ?? (tempSelected ? 1 : 0)}
      isFiltered={!!tempSelected}
      sortActive={sortActive}
      sortDirection={sortDirection}
      onSortChange={onSortChange}
      confirm={confirm}
      close={close}
      onApply={handleApply}
      onReset={handleResetInternal}
    >
      {before}
      <Flex justify="space-between" align="center" className={styles.toolbar}>
        <button
          type="button"
          className={styles.linkButton}
          onClick={handleClear}
          disabled={!tempSelected}
        >
          Clear selection
        </button>
        <span className={styles.displayCount}>
          Displaying {filteredOptions.length}
        </span>
      </Flex>

      <Input
        allowClear
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        suffix={<SearchOutlined className={styles.searchIcon} />}
        className={styles.searchInput}
      />

      <div className={styles.optionsList}>
        {loading ? (
          <Flex justify="center" align="center" className={styles.loading}>
            <Spin size="small" />
          </Flex>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <label key={option.value} className={styles.optionRow}>
              <Radio
                checked={tempSelected === option.value}
                onClick={() => selectOption(option.value)}
              />
              <span className={styles.optionLabel}>{option.label}</span>
            </label>
          ))
        ) : (
          <div className={styles.emptyState}>No results found</div>
        )}
      </div>
    </ColumnFilterWrapper>
  );
}

